import { useMemo, useState } from 'react'
import { Alert, Box, Button, Grid, Paper, Stack, TextField, Typography } from '@mui/material'
import { SectionLoader } from 'Components/Section'
import { useKoreanSchoolYearlyStatistics } from '../Hooks/useKoreanSchoolYearlyStatistics'

type Props = {
  onCancel: () => void
}

type YearlyValues = {
  year: string
  schoolCount: string
  universityCount: string
}

const initialForm: YearlyValues = {
  year: '',
  schoolCount: '',
  universityCount: '',
}

const initialTouched = {
  year: false,
  schoolCount: false,
  universityCount: false,
}

export const KoreanSchoolYearlyCreateForm = ({ onCancel }: Props) => {
  const [form, setForm] = useState<YearlyValues>(initialForm)
  const [touched, setTouched] = useState(initialTouched)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const { createMutation } = useKoreanSchoolYearlyStatistics()

  const rawErrors = useMemo(
    () => ({
      year: !form.year.trim() || Number(form.year) <= 0,
      schoolCount: !form.schoolCount.trim() || Number(form.schoolCount) < 0,
      universityCount: !form.universityCount.trim() || Number(form.universityCount) < 0,
    }),
    [form],
  )

  const visibleErrors = {
    year: rawErrors.year && (touched.year || submitAttempted),
    schoolCount: rawErrors.schoolCount && (touched.schoolCount || submitAttempted),
    universityCount: rawErrors.universityCount && (touched.universityCount || submitAttempted),
  }

  const isDisabled = rawErrors.year || rawErrors.schoolCount || rawErrors.universityCount || createMutation.isLoading

  const handleChange = (field: keyof YearlyValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    if (/^\d*$/.test(value)) {
      setForm(prev => ({ ...prev, [field]: value }))
      if (submitError) setSubmitError('')
    }
  }

  const handleBlur = (field: keyof typeof initialTouched) => () => {
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitAttempted(true)
    if (isDisabled) return

    createMutation.mutate(
      {
        year: Number(form.year),
        schoolCount: Number(form.schoolCount),
        universityCount: Number(form.universityCount),
      },
      {
        onSuccess: () => {
          setForm(initialForm)
          setTouched(initialTouched)
          setSubmitAttempted(false)
          onCancel()
        },
        onError: (error: any) => {
          setSubmitError(error?.response?.data?.message || 'Ошибка при сохранении статистики.')
        },
      },
    )
  }

  return (
    <SectionLoader isLoading={false} isFetching={false}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#fff',
        }}>
        <Stack spacing={3} component='form' onSubmit={handleSubmit}>
          <Box>
            <Typography variant='h5' fontWeight={700}>
              Добавить годовую статистику
            </Typography>
            <Typography variant='body2' color='text.secondary' mt={0.5}>
              Укажите год, количество школ и количество университетов.
            </Typography>
          </Box>

          {submitError && <Alert severity='error'>{submitError}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Год'
                value={form.year}
                onChange={handleChange('year')}
                onBlur={handleBlur('year')}
                error={visibleErrors.year}
                helperText={visibleErrors.year ? 'Введите корректный год.' : ' '}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Количество школ'
                value={form.schoolCount}
                onChange={handleChange('schoolCount')}
                onBlur={handleBlur('schoolCount')}
                error={visibleErrors.schoolCount}
                helperText={visibleErrors.schoolCount ? 'Введите корректное число (0 или больше).' : ' '}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Количество университетов'
                value={form.universityCount}
                onChange={handleChange('universityCount')}
                onBlur={handleBlur('universityCount')}
                error={visibleErrors.universityCount}
                helperText={visibleErrors.universityCount ? 'Введите корректное число (0 или больше).' : ' '}
              />
            </Grid>
          </Grid>

          <Stack direction='row' spacing={1.5} justifyContent='flex-end'>
            <Button variant='outlined' color='inherit' onClick={onCancel} disabled={createMutation.isLoading}>
              Отмена
            </Button>
            <Button type='submit' variant='contained' disabled={isDisabled}>
              {createMutation.isLoading ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </SectionLoader>
  )
}
